import { MessageSquareQuote } from "lucide-react";
import QuestionBadge from "./QuestionBadge";

/**
 * Shared loading + empty visuals for any block that displays answered
 * questions. Mirrors the peach tint, dashed primary border, left accent
 * stripe, and quote-mark watermark used by AnsweredQuestionCallout /
 * FeaturedAnswers so users see the same "this is a question surface"
 * treatment whether data is loading, empty, or populated.
 */

const SHELL_CLASSES = [
  "relative overflow-hidden rounded-md p-4 sm:p-5 pl-5 sm:pl-7",
  "bg-[hsl(var(--primary)/0.08)]",
  "border-2 border-dashed border-primary/60",
  "shadow-[4px_4px_0_0_hsl(var(--primary)/0.35)]",
].join(" ");

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className={SHELL_CLASSES} aria-busy="true">
      <span aria-hidden className="absolute left-0 top-0 bottom-0 w-1.5 bg-primary" />
      <MessageSquareQuote
        aria-hidden
        className="absolute -top-2 -right-2 w-20 h-20 text-primary/10 rotate-12 pointer-events-none"
      />
      {children}
    </div>
  );
}

export function QuestionBlockSkeleton({
  rows = 4,
  label = "Loading answered questions",
}: {
  rows?: number;
  label?: string;
}) {
  return (
    <Shell>
      <div className="flex items-center justify-between gap-2 mb-3 relative">
        <QuestionBadge label={label} icon="sparkles" />
        <div className="h-3 w-20 rounded-sm skeleton-shimmer" aria-hidden />
      </div>
      <ul className="space-y-2 relative" aria-hidden>
        {Array.from({ length: rows }).map((_, i) => (
          <li
            key={i}
            className="flex items-center gap-2 py-1.5 px-2 -mx-2 rounded"
          >
            <MessageSquareQuote className="w-3.5 h-3.5 shrink-0 text-primary/30" />
            <div
              className="h-4 rounded-sm skeleton-shimmer"
              style={{ width: `${65 + ((i * 13) % 30)}%` }}
            />
            <div className="ml-auto h-3 w-10 rounded-sm skeleton-shimmer shrink-0" />
          </li>
        ))}
      </ul>
      <span className="sr-only">{label}…</span>
    </Shell>
  );
}

export function QuestionBlockEmpty({
  title = "No answered questions yet",
  description,
  action,
  badgeLabel = "Answered by the pods",
}: {
  title?: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  badgeLabel?: string;
}) {
  return (
    <Shell>
      <div className="flex items-center gap-2 mb-3 relative">
        <QuestionBadge label={badgeLabel} icon="sparkles" />
      </div>
      <div className="relative">
        <p className="question-title text-base sm:text-lg mb-1">{title}</p>
        {description && (
          <p className="font-mono text-xs text-muted-foreground">{description}</p>
        )}
        {action && <div className="mt-3">{action}</div>}
      </div>
    </Shell>
  );
}
